import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import { History, RotateCw } from "lucide-react";
import styled from "styled-components";
import { AccountingCard as Card, KpiLabel, ReserveRefreshButton } from "./styled";
import { formatReserveAmount, type RollingReserveLifetimeCurrency } from "./rollingReserveData";

const CurrencyMetrics = styled.div`
	display: grid; flex: 1;
	grid-template-columns: repeat(auto-fit, minmax(min(100%, 220px), 1fr));
	gap: 12px; margin-top: 16px;
`;
const LifetimeCurrency = styled.section`
	min-width: 0; padding: 12px 14px; border-radius: 10px; background: #f5f3ff;
	h4 { margin: 0 0 12px; color: #667085; font-size: 12px; font-weight: 400; }
	dl { margin: 0; display: flex; flex-direction: column; gap: 8px; }
	dl > div { display: flex; align-items: baseline; justify-content: space-between; flex-wrap: wrap; gap: 4px 12px; }
	dt { color: #667085; font-size: 12px; }
	dd { margin: 0; max-width: 100%; font-size: 18px; font-weight: 600; color: #1a1d26; font-variant-numeric: tabular-nums; overflow-wrap: anywhere; }
`;

interface Props {
	busy: boolean;
	rows?: RollingReserveLifetimeCurrency[] | null;
	error?: string;
	asOf?: string;
	channelFiltered?: boolean;
	onRefresh: () => void;
}

export function RollingReserveLifetimeCard({ busy, rows, error, asOf, channelFiltered = false, onRefresh }: Props) {
	useLocale();
	const visibleRows = busy || error ? null : rows;
	return <Card style={{ height: "auto", minWidth: 0 }} role="region" aria-label={msg("rollingReserveLifetime")} aria-busy={busy}>
		<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
			<div style={{ display: "flex", alignItems: "center", gap: 12 }}>
				<div style={{ padding: 10, borderRadius: 12, color: "#6d5ce8", background: "#f1effd", display: "flex" }}><History size={20} /></div>
				<div>
					<div style={{ fontSize: 14, fontWeight: 600, color: "#1a1d26" }}>{msg("rollingReserveLifetime_0c8a6a25")}</div>
					<KpiLabel>{asOf ? msg("templateAsOf_4096bd2d", { v0: asOf }) : msg("allTime")}</KpiLabel>
					{channelFiltered && <KpiLabel style={{ display: "block" }}>{msg("reserveCoversAllChannels")}</KpiLabel>}
				</div>
			</div>
			<ReserveRefreshButton $busy={busy} title={error ? msg("retry") : msg("refresh")} aria-label={error ? msg("retry") : msg("refresh")} disabled={busy} onClick={onRefresh}><RotateCw size={16} aria-hidden="true" /></ReserveRefreshButton>
		</div>
		<div role="status" aria-live="polite">
			{busy && <KpiLabel style={{ display: "block", marginTop: 12 }}>{msg("loadingReserves")}</KpiLabel>}
			{error && <div style={{ marginTop: 12, color: "#b42332", fontSize: 13 }}>{error}</div>}
			{!busy && !error && !visibleRows?.length && <KpiLabel style={{ display: "block", marginTop: 12 }}>{visibleRows ? msg("noLifetimeReserveActivity") : msg("lifetimeReserveDataIsUnavailable")}</KpiLabel>}
		</div>
		{!!visibleRows?.length && <CurrencyMetrics>
			{visibleRows.map(row => <LifetimeCurrency key={row.currency} aria-label={msg("templateLifetimeReserves_1ee70d02", { v0: row.currency })}>
				<h4>{row.currency}</h4>
				<dl>
					<div><dt>{msg("reservedAllTime")}</dt><dd>{formatReserveAmount(row.taken)}</dd></div>
					<div><dt>{msg("releasedAllTime")}</dt><dd>{formatReserveAmount(row.released)}</dd></div>
				</dl>
			</LifetimeCurrency>)}
		</CurrencyMetrics>}
	</Card>;
}
